import { Component, effect, EventEmitter, OnDestroy, OnInit, Output, signal, ViewChild, WritableSignal } from '@angular/core';
import { ReportSpecification, CommonToolbarConfiguration, CommonToolbarResult } from '@microsec/models';
import { BaseComponent } from '@ids-components';
import {
  CardComponent,
  CommonTableComponent,
  CommonToolbarComponent,
  OperationButtonComponent,
  RefreshButtonComponent,
  TableColumnFilterComponent,
} from '@microsec/components';
import { AssessmentService } from '@ids-services';
import { PER_PAGE, DELETE_LABEL, MODULE_CONSTANTS } from '@microsec/constants';
import { ConstantPipe, MomentPipe } from '@ids-pipes';
import { ASSESSMENT_REPORT_STATUS, ASSESSMENT_STATUS, PROJECT_MANAGEMENT_CONSTANTS, QUOTAS_FEATURES } from '@ids-constants';
import { ORGANIZATION_ID_PARAM_ROUTE, PROJECT_ID_PARAM_ROUTE } from '@microsec/constants';
import { finalize, firstValueFrom, forkJoin, switchMap } from 'rxjs';
import { LazyLoadEvent } from 'primeng/api';
import moment from 'moment';
import { authenticationSelectors } from '@microsec/ngrx-authentication';
import { AssessmentPreviewComponent } from '../assessment-preview/assessment-preview.component';
import { ASSESSMENT_TOOL_CONSTANTS } from '../../assessment-tool.config';
import { SharedAssessmentReportComponent } from 'apps/ids-frontend/src/app/products/shared-components/shared-assessment-report/shared-assessment-report.component';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { AssessmentDetailsComponent } from './assessment-details/assessment-details.component';

const FIELDS = {
  id: 'ID',
  name: 'Name',
  assessment_type: 'Type',
  status: 'Status',
  scheduled: 'Scheduled Date',
};

@Component({
  selector: 'app-assessment-list',
  templateUrl: './assessment-list.component.html',
  styleUrls: ['./assessment-list.component.scss'],
  imports: [
    CommonModule,
    TooltipModule,
    ButtonModule,
    CommonToolbarComponent,
    CardComponent,
    CommonTableComponent,
    OperationButtonComponent,
    RefreshButtonComponent,
    TableColumnFilterComponent,
    MomentPipe,
    ConstantPipe,
    AssessmentDetailsComponent,
  ],
  providers: [MomentPipe],
})
export class AssessmentListComponent extends BaseComponent implements OnInit, OnDestroy {
  isLoading = true;

  currentPage = 1;

  totalRecords = 0;

  @Output() openAssessmentFormEvent: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('dt') dt!: CommonTableComponent;

  values: any[] = [];

  cols = [
    { field: 'id', header: FIELDS.id, width: 8 },
    { field: 'name', header: FIELDS.name, width: 15 },
    { field: 'assessment_type', header: FIELDS.assessment_type, width: 15 },
    { field: 'status', header: FIELDS.status, width: 15 },
    { field: 'scheduled', header: FIELDS.scheduled, width: 15 },
    { field: 'actions', header: 'Actions', width: 15, frozen: true },
  ];

  selectedAssessments: any[] = [];

  selectedAssessment: any = null;

  filterObject: WritableSignal<CommonToolbarResult | null> = signal(null);

  selectedCols: any[] = [];

  _selectedColFields: string[] = [];

  get selectedColFields(): string[] {
    return this._selectedColFields;
  }

  set selectedColFields(value: string[]) {
    this._selectedColFields = value;
    this.selectedCols = (this.cols || []).filter((col) => value?.includes(col.field));
  }

  filterConfiguration: CommonToolbarConfiguration = {
    types: ['search'],
    searchPlaceholder: 'Search Name, or Description...',
    hideClearFilters: false,
  };

  filterSearch = '';

  showPreview = false;

  executedAssessment: any = null;

  ASSESSMENT_STATUS = ASSESSMENT_STATUS;

  ASSESSMENT_REPORT_STATUS = ASSESSMENT_REPORT_STATUS;

  currentUser: any = null;

  getAssessmentsInterval: any;

  constructor(
    private momentPipe: MomentPipe,
    private assessmentSrv: AssessmentService,
  ) {
    super();
    effect(() => {
      const values = this.filterObject();
      if (!!values) {
        this.filterSearch = values.search || '';
        if (!!values?.isFiltered) {
          this.currentPage = 1;
          if (this.dt?.datatable) {
            this.dt.datatable.first = 0;
          }
        }
        if (values?.isSortReset && this.dt?.datatable) {
          this.dt.datatable.sortField = null;
          this.dt.datatable.sortOrder = 1;
          this.dt.datatable.multiSortMeta = null;
          this.dt?.datatable.tableService.onSort(null);
        }
        this.getAssessments();
      }
    });
  }

  async ngOnInit() {
    await this.prepareConfigs();
    this.currentUser = await firstValueFrom(this.store.select(authenticationSelectors.currentUser));
    this.selectedColFields = (this.cols || []).map((col) => col.field);
    this.getAssessments();
    this.getAssessmentsInterval = setInterval(() => {
      this.getAssessments(false, false);
    }, 30000);
    this.subscriptions.forEach((s) => s.unsubscribe());
    const subscription = this.assessmentSrv.refreshObs.subscribe((rs) => {
      if (!!rs) {
        this.getAssessments();
      }
    });
    this.subscriptions.push(subscription);
  }

  async getAssessments(resetSelectedAssessments = true, showLoading = true, event?: LazyLoadEvent) {
    await this.prepareConfigs();
    if (showLoading) {
      this.isLoading = true;
    }
    const page = !event ? this.currentPage : Math.floor((event as any)?.first / (event?.rows as number)) + 1;
    const perPage = event?.rows || this.dt?.datatable?.rows || PER_PAGE;
    this.assessmentSrv
      .getAssessments(
        this.breadcrumbConfig?.organizationId,
        this.breadcrumbConfig?.projectId,
        page,
        perPage,
        this.dt?.datatable?.sortField as any,
        this.dt?.datatable?.sortOrder ? this.dt?.datatable?.sortOrder === 1 : true,
        this.filterSearch,
      )
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (res: any) => {
          this.currentPage = res?.page;
          this.totalRecords = res?.total_record;
          const assessments = (res.assessments as any[]) || [];
          this.values = assessments;
          if (!!this.selectedAssessment) {
            this.selectedAssessment = this.values.find((a) => a.id === this.selectedAssessment.id) || null;
          }
          if (!!resetSelectedAssessments) {
            this.selectedAssessments = [];
          }
        },
        error: (error) => {
          this.showErrorMessage(error);
        },
      });
  }

  openGenerateReportDialog() {
    const onGenerateReport = (specification: ReportSpecification) => {
      if (!!specification) {
        this.dt.generateReportDialog.exportReport(specification.data || [], `project_${this.breadcrumbConfig?.projectId}_assessments`);
      }
    };
    this.dt.generateReportDialog.open(onGenerateReport, FIELDS, this.selectedCols, [], this.values);
  }

  openAssessmentForm(assessment?: any) {
    this.openAssessmentFormEvent?.emit(assessment);
  }

  openDeleteConfirmation(assessments: any[] = []) {
    const assessmentList = assessments.map((q) => `<li>Assessment ID: ${q.id}</li>`) || [];
    this.confirm({
      action: DELETE_LABEL,
      objectName: 'Assessment(s)',
      customContent: `Are you sure you want to delete ${
        !assessments?.length ? 'all assessments?<br/><br/>' : `the following assessment(s)?<ul>${assessmentList.join('')}</ul>`
      }`,
      prepareRequest: () => {
        this.isLoading = true;
      },
      acceptRequest: forkJoin((!!assessments.length ? assessments : this.values).map((q) => this.assessmentSrv.deleteAssessment(q.id))),
      next: () => {
        this.isLoading = false;
        this.showSuccessMessage(`Deleted assessment(s) successfully`);
        const assessmentIds = assessments.map((assessment) => assessment.id) || [];
        if (!!this.selectedAssessments?.length) {
          this.selectedAssessments = this.selectedAssessments.filter((assessment) => !assessmentIds?.includes(assessment.id));
        }
        if (!!this.selectedAssessment && !!assessmentIds.includes(this.selectedAssessment.id)) {
          this.selectedAssessment = null;
        }
        this.getAssessments();
      },
      error: (err) => {
        this.isLoading = false;
        this.showErrorMessage(err);
      },
    });
  }

  openAssessmentReport(assessment: any) {
    this.dialogSrv.open(SharedAssessmentReportComponent, {
      data: { assessment },
      closeOnEscape: false,
      showHeader: false,
      modal: false,
      draggable: false,
      resizable: false,
      closable: false,
      styleClass: 'dialog-full-screen',
    });
  }

  openAssessmentPreview(assessment: any) {
    const dialog = this.dialogSrv.open(AssessmentPreviewComponent, {
      data: { assessment },
      closeOnEscape: false,
      showHeader: false,
      modal: false,
      draggable: false,
      resizable: false,
      closable: false,
      styleClass: 'dialog-full-screen',
    });
    dialog.onClose.subscribe((rs) => {
      if (!!rs) {
        setTimeout(() => {
          if (rs.report_status === ASSESSMENT_REPORT_STATUS.COMPLETED) {
            const completedAssessment = {
              ...assessment,
              ...rs,
            };
            this.openAssessmentReport(completedAssessment);
          }
        }, 10);
        this.assessmentSrv.refresh$.next(rs);
      }
    });
  }

  openExecutePanel(assessment: any) {
    if (assessment.status === ASSESSMENT_STATUS.OPEN && !!this.permissions[this.USER_ROLE.TECHNICIAN]) {
      this.prefillAssessment(assessment);
    } else {
      this.openAssessmentPreview(assessment);
    }
  }

  prefillAssessment(assessment: any) {
    this.isLoading = true;
    this.assessmentSrv
      .updateAssessment(assessment.id, { status: ASSESSMENT_STATUS.ACTIVE })
      .pipe(
        switchMap(() => this.assessmentSrv.prefillAssessment(assessment.id)),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: (rs) => {
          this.showSuccessMessage('Assessment Prefilled Successfully');
          this.openAssessmentPreview({
            ...assessment,
            questions: (rs as any[]) || [],
          });
        },
        error: (err) => {
          this.showErrorMessage(err);
        },
      });
  }

  updateAssessment(assessment: any, payload: any, onSuccess?: (rs: any) => void, onFailed?: (err: any) => void) {
    this.isLoading = true;
    this.assessmentSrv
      .updateAssessment(assessment.id, payload)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (rs) => {
          onSuccess?.(rs);
        },
        error: (err) => {
          onFailed?.(err);
        },
      });
  }

  goToPage(type: 'preview' | 'report', assessment: any) {
    this.changeRoute(
      `${MODULE_CONSTANTS.PROJECT_MANAGEMENT.ROUTE.replace(ORGANIZATION_ID_PARAM_ROUTE, this.breadcrumbConfig?.organizationId?.toString()).replace(
        PROJECT_ID_PARAM_ROUTE,
        this.breadcrumbConfig?.projectId?.toString(),
      )}/${PROJECT_MANAGEMENT_CONSTANTS.ASSESSMENT_TOOL.ROUTE}/${ASSESSMENT_TOOL_CONSTANTS.ASSESSMENTS.ROUTE}/${(type === 'preview'
        ? ASSESSMENT_TOOL_CONSTANTS.ASSESSMENT_PREVIEW
        : ASSESSMENT_TOOL_CONSTANTS.ASSESSMENT_REPORT
      ).ROUTE.replace(':assessmentId', assessment.id)}`,
    );
  }

  execute(assessment: any) {
    if (!!assessment) {
      if (
        assessment.status === ASSESSMENT_STATUS.OPEN &&
        moment.utc(assessment.scheduled).local() > moment.utc().local() &&
        !!this.permissions[this.USER_ROLE.TECHNICIAN]
      ) {
        this.openExecuteConfirmation(assessment);
      } else {
        this.openExecutePanel(assessment);
      }
    }
  }

  openExecuteConfirmation(assessment: any) {
    this.confirm({
      action: 'Start',
      objectName: 'Assessment',
      customContent: `This assessment is scheduled for ${this.momentPipe?.transform(
        assessment.scheduled,
      )}.<br/><br/>Are you sure you want to start assessment early?`,
      next: () => {
        this.openExecutePanel(assessment);
      },
    });
  }

  hideDetailsPanel(evt?: any) {
    if (!!evt && (evt.target.localName !== 'div' || (evt.target.localName === 'div' && evt.target.id !== 'tb'))) {
      return;
    }
    this.selectedAssessment = null;
  }

  get checkAssessmentQuota() {
    return this.values.length < this.getQuotaLimitation(QUOTAS_FEATURES.ASSESSMENTS);
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.cleanupDialogs();
    clearInterval(this.getAssessmentsInterval);
    this.assessmentSrv.refresh$.next(null);
  }
}
